import { cache } from "react";

export const BLOG_API_BASE = "https://www.nohaya.com/blog/api";

export type BlogField = "resumes" | "prompts" | "places" | "apps";

export type Faq = { question: string; answer: string };
export type Reference = { label: string; url: string };
export type ToolCard = {
  name: string;
  logo_url?: string;
  description?: string;
  pros?: string[];
  cons?: string[];
  pricing?: string;
  website_url?: string;
  rating?: number;
};

export type BlogPost = {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  field: BlogField;
  tags: string[];
  cover_gradient: string;
  cover_image_url: string | null;
  author: string;
  status: "draft" | "published";
  is_ai_generated: number;
  read_time_minutes: number;
  view_count: number;
  published_at: string;
  created_at: string;
  updated_at: string;
  // Only populated by get.php/admin list.php (SELECT *); the public list.php
  // endpoint used for cards/related-posts/feed uses an explicit column list
  // and omits these, so treat them as possibly absent everywhere.
  faqs?: Faq[];
  key_takeaways?: string[];
  audience_fit?: string[];
  audience_avoid?: string[];
  references?: Reference[];
  tools?: ToolCard[];
};

export type FieldMeta = {
  id: BlogField;
  label: string;
  icon: string;
  gradient: string;
  href: string;
};

export const FIELDS: FieldMeta[] = [
  { id: "resumes", label: "Resumes", icon: "📄", gradient: "from-violet-500 to-fuchsia-500", href: "/resumes" },
  { id: "prompts", label: "AI Prompts", icon: "🎨", gradient: "from-blue-500 to-purple-500", href: "/prompts" },
  { id: "places", label: "Travel", icon: "🧭", gradient: "from-cyan-500 to-emerald-500", href: "/places" },
  { id: "apps", label: "AI Tools", icon: "⚡", gradient: "from-amber-500 to-orange-500", href: "/apps" },
];

export function getFieldMeta(field: string): FieldMeta {
  return FIELDS.find((f) => f.id === field) ?? FIELDS[0];
}

// Plain fetch (default caching) — `cache: "no-store"` is incompatible with
// `output: "export"` (it forces Next into dynamic-route mode, which throws a
// NEXT_STATIC_GEN_BAILOUT during static export). To get fresh blog content on
// every rebuild, the deploy script clears .next/cache before each `next build`
// instead of fighting the fetch cache here.
export async function getAllPosts(): Promise<BlogPost[]> {
  try {
    const res = await fetch(`${BLOG_API_BASE}/posts/list.php?limit=100`, {
      signal: AbortSignal.timeout(5000),
      next: { tags: ["blog-posts"] },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.success && Array.isArray(data.posts) ? data.posts : [];
  } catch {
    return [];
  }
}

// Wrapped in React's cache() so generateMetadata and the page component (which
// both call this for the same slug during one render) share a single request
// instead of firing two concurrent calls — the get.php endpoint also increments
// view_count, and two simultaneous UPDATEs on the same row could race/fail.
export const getPostBySlug = cache(async (slug: string): Promise<BlogPost | null> => {
  try {
    const res = await fetch(`${BLOG_API_BASE}/posts/get.php?slug=${encodeURIComponent(slug)}`, {
      signal: AbortSignal.timeout(5000),
      next: { tags: ["blog-posts", `blog-post-${slug}`] },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.success ? data.post : null;
  } catch {
    return null;
  }
});

export function formatPostDate(post: BlogPost): string {
  return (post.published_at || post.created_at).slice(0, 10);
}
